import React, { useState, useEffect } from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import "./App.css";
import { ThemeProvider } from "./context/ThemeContext";
import { useLocationTracking } from "./hooks/useLocationTracking";
import Layout from "./components/Layout";
import OnboardingWizard from "./components/OnboardingWizard";
import Overview from "./pages/Overview";
import Customers from "./pages/CustomersEnhanced";
import JobList from "./pages/JobList";
import VisualPlanner from "./pages/VisualPlanner";
import VisualPlannerPage from "./pages/VisualPlannerPage";
import WorkPlanner from "./pages/WorkPlanner";
import Settings from "./pages/Settings";
import ConnectedApps from "./pages/ConnectedApps";
import Quotes from "./pages/Quotes";
import Payments from "./pages/Payments";
import UsersAndTeams from "./pages/UsersAndTeams";
import Rounds from "./pages/Rounds";
import Services from "./pages/Services";
import Reporting from "./pages/Reporting";
import Invoices from "./pages/Invoices";
import Expenses from "./pages/Expenses";
import ChartOfAccounts from "./pages/ChartOfAccounts";
import ExpenseCategories from "./pages/ExpenseCategories";
import DayPilot from "./pages/DayPilot";
import Owings from "./pages/Owings";
import SendPage from "./pages/SendPage";
import CustomerInvoicePage from "./pages/CustomerInvoicePage";
import PayAccountPage from "./pages/PayAccountPage";
import BankConnectedPage from "./pages/BankConnectedPage";
import LoginPage from "./pages/LoginPage";
import PricingPage from "./pages/PricingPage";
import StripeCustomerSync from "./pages/StripeCustomerSync";
import MessagesChat from "./pages/MessagesChat";
import GoCardlessCustomerSync from "./pages/GoCardlessCustomerSync";
import SyncCentre from "./pages/SyncCentre";
import Empire from "./pages/Empire";
import BankTransactions from "./pages/BankTransactions";
import ApiDocs from "./pages/ApiDocs";
import Leads from "./pages/Leads";
import Canvassing from "./pages/Canvassing";
import Learning from "./pages/Learning";
import Waitlist from "./pages/Waitlist";
import CustomerDetailPage from "./pages/CustomerDetailPage";
import HelpAssistant from "./components/HelpAssistant";
import AIStaffAssistant from "./components/AIStaffAssistant";
import AuthCallback from "./components/AuthCallback";
import NewJobModal from "./components/NewJobModal";
import SMSSettings from "./pages/Settings/SMSSettings";
import StaffLeave from "./pages/StaffLeave";
import WorkerLocations from "./pages/WorkerLocations";
import TimeCompletedReport from "./pages/TimeCompletedReport";
import SqueegeeSync from "./pages/SqueegeeSync";
import ChargeAudit from "./pages/ChargeAudit";
import PayRedirect from "./pages/PayRedirect";
import ExpiredBankBanner from "./components/ExpiredBankBanner";
import SmsVerifyBanner from "./components/SmsVerifyBanner";
import * as api from "./services/api";
import useSwipeBack from "./utils/useSwipeBack";

// Placeholder pages for routes not yet implemented
const PlaceholderPage = ({ title }) => (
  <div className="h-full flex items-center justify-center bg-gray-50">
    <div className="text-center">
      <h2 className="text-2xl font-semibold text-zinc-800 mb-2">{title}</h2>
      <p className="text-gray-500">This feature is coming soon</p>
    </div>
  </div>
);

function App({ user, onLogout, initialPage = 'overview' }) {
  // Workers should go straight to work-planner
  const userRole = user?.role || 'Owner';
  const isWorker = userRole === 'Worker';
  
  // Restore last page from localStorage, or use default
  const getInitialPage = () => {
    const savedPage = localStorage.getItem('streakfree_current_page');
    if (savedPage && savedPage !== 'login') {
      return savedPage;
    }
    return isWorker ? 'work-planner' : initialPage;
  };
  
  const [currentPage, setCurrentPage] = useState(getInitialPage);
  const [pageHistory, setPageHistory] = useState(['overview']); // Track navigation history
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [workPlannerDate, setWorkPlannerDate] = useState(null);
  const [showNewJobModal, setShowNewJobModal] = useState(false);
  const [showAIStaff, setShowAIStaff] = useState(false);

  // Navigate with history tracking
  const navigateToPage = (page) => {
    // Handle AI Staff as a popup
    if (page === 'help-assistant') {
      setShowAIStaff(true);
      return;
    }
    
    if (page !== currentPage) {
      setPageHistory(prev => [...prev, currentPage]); // Save current page to history
      setCurrentPage(page);
    }
  };

  // Go back to previous page
  const goBack = () => {
    if (pageHistory.length > 0) {
      const prevPage = pageHistory[pageHistory.length - 1];
      setPageHistory(prev => prev.slice(0, -1)); // Remove last page from history
      setCurrentPage(prevPage);
    }
  };

  // Listen for global navigate events dispatched from anywhere (e.g. WorkPlanner FAB)
  useEffect(() => {
    const handler = (e) => {
      const page = e?.detail?.page;
      if (page) navigateToPage(page);
    };
    window.addEventListener('streakfree:navigate', handler);
    return () => window.removeEventListener('streakfree:navigate', handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, pageHistory]);

  // Enable swipe-back navigation on all pages
  useSwipeBack({ 
    threshold: 70, 
    edgeWidth: 40,  // 40px edge zone for iOS
    onSwipeBack: goBack
  });

  // Save current page to localStorage whenever it changes
  useEffect(() => {
    if (currentPage && currentPage !== 'login') {
      localStorage.setItem('streakfree_current_page', currentPage);
    }
  }, [currentPage]);
  
  // Global persistent location tracking. Reads the opt-in flag from
  // localStorage on every app mount → if the user previously enabled
  // tracking, watchPosition auto-resumes WITHOUT re-prompting (browser keeps
  // the permission grant for the origin). This means workers don't have to
  // remember to re-enable tracking every morning, and arrival/departure
  // detection keeps working across PWA close/reopen cycles.
  useLocationTracking();

  // Handle navigation from header - intercept 'jobs' to show modal
  const handleHeaderNavigate = (page) => {
    if (page === 'jobs') {
      setShowNewJobModal(true);
    } else {
      navigateToPage(page);
    }
  };

  const renderPage = () => {
    switch (currentPage) {
      case 'overview':
        return <Overview onNavigate={navigateToPage} user={user} />;
      case 'visual-planner':
        return <VisualPlannerPage onBack={goBack} />;
      case 'my-work':
        return <PlaceholderPage title="My Work" />;
      case 'day-pilot':
        return <DayPilot onBack={goBack} user={user} />;
      case 'empire':
        return <Empire 
          user={user} 
          onSelectCustomer={(customer) => {
            setSelectedCustomer(customer);
            navigateToPage('customer-detail');
          }}
        />;
      case 'work-planner':
        return <WorkPlanner initialDate={workPlannerDate} onDateNavigated={() => setWorkPlannerDate(null)} user={user} />;
      case 'multi-planner':
        return <PlaceholderPage title="Multi Planner" />;
      case 'jobs':
      case 'job-list':
        return <JobList onNavigate={navigateToPage} />;
      case 'overdue':
        return <JobList />;
      case 'customers':
        return <Customers 
          onSelectCustomer={(customer) => {
            setSelectedCustomer(customer);
            navigateToPage('customer-detail');
          }}
          onNavigateToWorkPlanner={(date) => {
            setWorkPlannerDate(date);
            navigateToPage('work-planner');
          }}
        />;
      case 'customer-detail':
        return selectedCustomer ? (
          <Customers 
            initialCustomer={selectedCustomer} 
            onBack={() => {
              setSelectedCustomer(null);
              goBack();
            }}
            onNavigateToWorkPlanner={(date) => {
              setWorkPlannerDate(date);
              navigateToPage('work-planner');
            }}
            onNavigate={navigateToPage}
          />
        ) : <Customers 
          onNavigateToWorkPlanner={(date) => {
            setWorkPlannerDate(date);
            navigateToPage('work-planner');
          }}
          onNavigate={navigateToPage}
        />;
      case 'leads':
        return <Leads onNavigate={navigateToPage} />;
      case 'canvassing':
        return <Canvassing user={user} />;
      case 'owing':
        return <Owings 
          onBack={goBack} 
          onSelectCustomer={(customer) => {
            setSelectedCustomer(customer);
            navigateToPage('customer-detail');
          }}
          user={user}
          setCurrentPage={navigateToPage}
        />;
      case 'pending':
        return <JobList />;
      case 'job-pricing':
        return <Services />;
      case 'send':
        return <SendPage onBack={goBack} />;
      case 'inbox':
      case 'messages':
        return <MessagesChat onNavigate={navigateToPage} />;
      case 'quotes':
        return <Quotes />;
      case 'invoices':
        return <Invoices onBack={goBack} />;
      case 'expenses':
        return <Expenses onBack={goBack} />;
      case 'expense-categories':
        return <ExpenseCategories onBack={goBack} />;
      case 'chart-of-accounts':
        return <ChartOfAccounts onBack={goBack} />;
      case 'payments':
        return <Payments />;
      case 'reporting':
        return <Reporting onBack={goBack} />;
      case 'staff-availability':
        return <StaffLeave onBack={goBack} />;
      case 'staff-leave':
        return <StaffLeave onBack={goBack} />;
      case 'worker-locations':
        return <WorkerLocations onBack={goBack} user={user} />;
      case 'time-completed':
        return <TimeCompletedReport onBack={goBack} />;
      case 'marketplace':
        return <ConnectedApps onBack={goBack} setCurrentPage={navigateToPage} />;
      case 'audit-log':
        return <PlaceholderPage title="Audit Log" />;
      case 'device-audit':
        return <PlaceholderPage title="Device Audit" />;
      case 'users':
        return <UsersAndTeams />;
      case 'teams':
        return <UsersAndTeams />;
      case 'rounds':
        return <Rounds />;
      case 'services':
        return <Services />;
      case 'attachments':
        return <PlaceholderPage title="Attachments" />;
      case 'portal-directory':
        return <PlaceholderPage title="Portal Directory" />;
      case 'training':
        return <PlaceholderPage title="Training" />;
      case 'learning':
        return <Learning />;
      case 'waitlist':
        return <Waitlist />;
      case 'help-assistant':
        return <AIStaffAssistant />;
      case 'about':
        return <PlaceholderPage title="About StreakFree" />;
      case 'sync-now':
        return <PlaceholderPage title="Syncing..." />;
      case 'reload-app':
        return <PlaceholderPage title="Reloading..." />;
      case 'settings':
        return <Settings onBack={goBack} onNavigate={navigateToPage} />;
      case 'sms-settings':
        return <SMSSettings onBack={goBack} />;
      case 'connected-apps':
        return <ConnectedApps onBack={goBack} setCurrentPage={navigateToPage} />;
      case 'sync-centre':
        return <SyncCentre onBack={goBack} setCurrentPage={navigateToPage} />;
      case 'bank-transactions':
        return <BankTransactions onBack={goBack} />;
      case 'pricing':
        return <PricingPage currentUser={user} />;
      case 'stripe-sync':
        return <StripeCustomerSync 
          onBack={goBack}
          onViewCustomer={(customerId) => {
            // Fetch the customer record then navigate to their detail page
            const token = localStorage.getItem('streakfree_token');
            fetch(`${process.env.REACT_APP_BACKEND_URL}/api/customers/${customerId}`, {
              headers: { 'Authorization': `Bearer ${token}` }
            })
              .then(r => r.ok ? r.json() : null)
              .then(c => {
                if (c) {
                  setSelectedCustomer(c);
                  navigateToPage('customer-detail');
                }
              })
              .catch(() => {});
          }}
        />;
      case 'gocardless-sync':
        return <GoCardlessCustomerSync onBack={goBack} />;
      case 'squeegee-sync':
        return <SqueegeeSync />;
      case 'charge-audit':
        return <ChargeAudit />;
      case 'api-docs':
        return <ApiDocs onBack={goBack} />;
      default:
        return <Overview user={user} />;
    }
  };

  return (
    <div className="App h-screen">
      <ExpiredBankBanner user={user} setCurrentPage={navigateToPage} />
      <SmsVerifyBanner user={user} />
      <OnboardingWizard onNavigate={navigateToPage} />
      <Layout 
        currentPage={currentPage} 
        setCurrentPage={navigateToPage}
        onHeaderNavigate={handleHeaderNavigate}
        user={user}
        onLogout={onLogout}
      >
        {renderPage()}
      </Layout>
      
      {/* New Job Modal - accessible from header */}
      {showNewJobModal && (
        <NewJobModal
          onClose={() => setShowNewJobModal(false)}
          onSave={(job) => {
            console.log('Job created:', job);
            // Could navigate to work planner or refresh current page
          }}
        />
      )}
      
      {/* AI Staff Assistant Popup */}
      {showAIStaff && (
        <AIStaffAssistant onClose={() => setShowAIStaff(false)} />
      )}
    </div>
  );
}

// Inner App Router that handles auth callback detection
function AppRouter() {
  const location = useLocation();
  const [user, setUser] = useState(null);
  const [isCheckingAuth, setIsCheckingAuth] = useState(true);
  const [initialPage, setInitialPage] = useState('overview');

  // Check for existing auth on mount and handle OAuth redirects
  useEffect(() => {
    const checkAuth = async () => {
      // Skip auth check if we're processing OAuth callback
      if (location.hash?.includes('session_id=')) {
        setIsCheckingAuth(false);
        return;
      }
      
      const storedUser = api.getStoredUser();
      if (storedUser && api.isAuthenticated()) {
        setUser(storedUser);
        
        // Check for integration callback in URL params
        const urlParams = new URLSearchParams(window.location.search);
        const page = urlParams.get('page');
        const integration = urlParams.get('integration');
        
        if (page === 'connected-apps' && integration) {
          setInitialPage('connected-apps');
          // Clean up URL
          window.history.replaceState({}, '', window.location.pathname);
        }

        // Path-based deep links: e.g. /customers?new=true should land on the
        // customers page regardless of the last-saved page in localStorage.
        const pathToPage = {
          '/customers': 'customers',
          '/work-planner': 'work-planner',
          '/day-pilot': 'day-pilot',
          '/jobs': 'jobs',
          '/dashboard': 'overview',
          '/owings': 'owings',
          '/leads': 'leads',
          '/canvassing': 'canvassing',
          '/rounds': 'rounds',
          '/reports': 'reports',
          '/settings': 'settings',
          '/messages': 'messages',
          '/inbox': 'messages',
          '/sync': 'sync-centre',
          '/integrations': 'connected-apps',
          '/tutorials': 'learning',
        };
        const pageFromPath = pathToPage[location.pathname];
        if (pageFromPath) {
          setInitialPage(pageFromPath);
          // Persist so the inner App's getInitialPage() also picks it up
          localStorage.setItem('streakfree_current_page', pageFromPath);
        }
      }
      setIsCheckingAuth(false);
    };
    checkAuth();
  }, [location.hash, location.pathname]);

  const handleLogin = async (userData) => {
    // Clear saved page from previous user session
    const prevUserId = localStorage.getItem('streakfree_last_user_id');
    const currentUserId = userData?.id || userData?.email;
    
    if (prevUserId && prevUserId !== currentUserId) {
      // Different user logging in - clear saved state
      localStorage.removeItem('streakfree_current_page');
      localStorage.removeItem('streakfree_notifications');
    }
    
    // Save current user ID for next comparison
    localStorage.setItem('streakfree_last_user_id', currentUserId);
    
    setUser(userData);
    
    // Initialize offline sync and trigger data sync in background
    import('./services/offlineSync').then(({ performFullSync, initOfflineSync }) => {
      // Set up online/offline event listeners
      initOfflineSync().catch(console.error);
      
      // Perform full data sync
      performFullSync((progress) => {
        console.log('Offline sync:', progress);
      }).then((result) => {
        if (result.success) {
          console.log(`Offline sync complete: ${result.customersCount} customers, ${result.jobsCount} jobs`);
        }
      }).catch(console.error);
    });
    
    // Check if we need to redirect to connected-apps after login
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('page') === 'connected-apps') {
      setInitialPage('connected-apps');
      window.history.replaceState({}, '', window.location.pathname);
    }
  };

  const handleLogout = async () => {
    // Clear saved page to prevent loading old page on next login
    localStorage.removeItem('streakfree_current_page');
    localStorage.removeItem('streakfree_notifications');
    await api.logout();
    setUser(null);
  };

  // Check URL fragment for session_id (Google OAuth callback)
  // This must be done synchronously during render to prevent race conditions
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback onLogin={handleLogin} />;
  }

  // Show loading while checking auth
  if (isCheckingAuth) {
    return (
      <div className="h-screen flex items-center justify-center bg-zinc-900">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-slate-400">Loading...</p>
        </div>
      </div>
    );
  }

  // Show login if not authenticated
  if (!user) {
    return <LoginPage onLogin={handleLogin} />;
  }

  // Check if we're on a customer detail page
  if (location.pathname.startsWith('/customers/') && location.pathname.length > 11) {
    return <CustomerDetailPage user={user} onLogout={handleLogout} />;
  }

  return <App user={user} onLogout={handleLogout} initialPage={initialPage} />;
}

import PaymentSetupPage from "./pages/PaymentSetupPage";
import JoinInvitePage from "./pages/JoinInvitePage";

// Main App with Router
function AppWithRouter() {
  return (
    <ThemeProvider>
      <BrowserRouter>
        <Routes>
          {/* Public invoice page - accessible without login */}
          <Route path="/invoice/:invoiceId" element={<CustomerInvoicePage />} />
          <Route path="/i/:invoiceId" element={<CustomerInvoicePage />} />

          {/* Public team invite page - accessible without login */}
          <Route path="/join/:token" element={<JoinInvitePage />} />
          
          {/* Salvage broken pay-now links from emails sent before BACKEND_URL
              was set on Render — bounce them to the backend redirect. */}
          <Route path="/api/payments/pay/:invoiceId" element={<PayRedirect />} />
          <Route path="/pay-account/:customerId" element={<PayAccountPage />} />
          <Route path="/bank-connected" element={<BankConnectedPage />} />
          
          {/* Public payment setup page - accessible without login */}
          <Route path="/payment-setup/:token" element={<PaymentSetupPage />} />
          
          {/* Auth callback route */}
          <Route path="/auth/callback" element={<AppRouter />} />
          
          {/* Main app */}
          <Route path="/*" element={<AppRouter />} />
        </Routes>
      </BrowserRouter>
    </ThemeProvider>
  );
}

export default AppWithRouter;
